import { baseInvoiceSchema, createInvoiceSchemaDefaultValues } from "./invoice";
import { z } from "zod";

// =============================================================================
// ENUMS
// =============================================================================

export const recurringFrequencyEnum = z.enum(["daily", "weekly", "monthly", "yearly", "custom"]);
export type RecurringFrequency = z.infer<typeof recurringFrequencyEnum>;

export const recurringEndTypeEnum = z.enum(["never", "after_occurrences", "by_date"]);
export type RecurringEndType = z.infer<typeof recurringEndTypeEnum>;

// =============================================================================
// SCHEDULING SCHEMA
// =============================================================================

export const recurringScheduleSchema = z.object({
  frequency: recurringFrequencyEnum,
  customIntervalDays: z.number().int().positive().optional().nullable(),
  dayOfWeek: z.number().int().min(0).max(6).optional().nullable(),
  dayOfMonth: z.number().int().min(1).max(31).optional().nullable(),
  monthOfYear: z.number().int().min(1).max(12).optional().nullable(),
  nextIssueDate: z.coerce.date(),
  sendTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, { message: "Invalid time format (HH:MM)" }),
  endType: recurringEndTypeEnum,
  endDate: z.coerce.date().optional().nullable(),
  maxOccurrences: z.number().int().positive().optional().nullable(),
  autoSend: z.boolean(),
  daysBeforeDue: z.number().int().min(0),
  // Email template fields (used when autoSend is true)
  emailTo: z.string().email().optional().nullable(),
  emailSubject: z.string().optional().nullable(),
  emailBody: z.string().optional().nullable(),
  emailCc: z.string().email().optional().nullable(),
  emailBcc: z.string().email().optional().nullable(),
});

export type RecurringScheduleSchema = z.infer<typeof recurringScheduleSchema>;

// =============================================================================
// BASE RECURRING TEMPLATE SCHEMA
// Extends from baseInvoiceSchema and adds recurring-specific fields
// =============================================================================

export const baseRecurringTemplateSchema = baseInvoiceSchema
  .omit({
    // Remove invoice-specific fields that don't apply to recurring templates
    invoiceNumber: true,
    estimateId: true,
    worklogIds: true,
    status: true,
    dueDate: true,
  })
  .extend({
    // Template name (unique to recurring)
    name: z.string().min(1, { message: "Template name is required" }),

    // Scheduling fields
    ...recurringScheduleSchema.shape,
  });

// =============================================================================
// CREATE SCHEMA
// =============================================================================

export const createRecurringTemplateSchema = baseRecurringTemplateSchema
  .refine(
    (data) => {
      // Validate custom interval is provided when frequency is 'custom'
      if (data.frequency === "custom") {
        return data.customIntervalDays && data.customIntervalDays > 0;
      }
      return true;
    },
    {
      message: "Custom interval days is required when frequency is 'custom'",
      path: ["customIntervalDays"],
    },
  )
  .refine(
    (data) => {
      // Validate end date is provided when end type is 'by_date'
      if (data.endType === "by_date") {
        return data.endDate != null;
      }
      return true;
    },
    {
      message: "End date is required when end type is 'by_date'",
      path: ["endDate"],
    },
  )
  .refine(
    (data) => {
      // Validate max occurrences is provided when end type is 'after_occurrences'
      if (data.endType === "after_occurrences") {
        return data.maxOccurrences && data.maxOccurrences > 0;
      }
      return true;
    },
    {
      message: "Max occurrences is required when end type is 'after_occurrences'",
      path: ["maxOccurrences"],
    },
  )
  .refine(
    (data) => {
      // Validate Swiss QR Bill bank account requirements
      if (data.showSwissQrBill) {
        return (
          data.organizationBankAccountId &&
          data.organizationBankAccountName &&
          data.organizationBankAccountCountry &&
          data.organizationBankAccountCurrency &&
          data.organizationBankAccountIban
        );
      }
      return true;
    },
    {
      message: "Bank account required for Swiss QR Bill",
      path: ["organizationBankAccountId"],
    },
  );

export type ZodCreateRecurringTemplateSchema = z.infer<typeof createRecurringTemplateSchema>;

// =============================================================================
// EDIT SCHEMA
// =============================================================================

export const editRecurringTemplateSchema = baseRecurringTemplateSchema
  .extend({
    id: z.string(),
  })
  .refine(
    (data) => {
      if (data.frequency === "custom") {
        return data.customIntervalDays && data.customIntervalDays > 0;
      }
      return true;
    },
    {
      message: "Custom interval days is required when frequency is 'custom'",
      path: ["customIntervalDays"],
    },
  )
  .refine(
    (data) => {
      if (data.endType === "by_date") {
        return data.endDate != null;
      }
      return true;
    },
    {
      message: "End date is required when end type is 'by_date'",
      path: ["endDate"],
    },
  )
  .refine(
    (data) => {
      if (data.endType === "after_occurrences") {
        return data.maxOccurrences && data.maxOccurrences > 0;
      }
      return true;
    },
    {
      message: "Max occurrences is required when end type is 'after_occurrences'",
      path: ["maxOccurrences"],
    },
  )
  .refine(
    (data) => {
      if (data.showSwissQrBill) {
        return (
          data.organizationBankAccountId &&
          data.organizationBankAccountName &&
          data.organizationBankAccountCountry &&
          data.organizationBankAccountCurrency &&
          data.organizationBankAccountIban
        );
      }
      return true;
    },
    {
      message: "Bank account required for Swiss QR Bill",
      path: ["organizationBankAccountId"],
    },
  );

export type ZodEditRecurringTemplateSchema = z.infer<typeof editRecurringTemplateSchema>;

// =============================================================================
// LIST PARAMS SCHEMA
// =============================================================================

export const getRecurringTemplatesParamsSchema = z.object({
  // Entity filters
  relationId: z.string().optional(),
  // Text search filters
  name: z.string().optional(),
  client: z.string().optional(),
  // Option filters
  frequency: z
    .union([recurringFrequencyEnum, z.array(recurringFrequencyEnum)])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  isActive: z.coerce.boolean().optional(),
  // Date range filters
  nextIssueDateFrom: z.coerce.date().optional(),
  nextIssueDateTo: z.coerce.date().optional(),
  createdAtFrom: z.coerce.date().optional(),
  createdAtTo: z.coerce.date().optional(),
});

export type ZodGetRecurringTemplatesParams = z.infer<typeof getRecurringTemplatesParamsSchema>;

// =============================================================================
// TOGGLE STATUS SCHEMA
// =============================================================================

export const toggleRecurringTemplateSchema = z.object({
  isActive: z.boolean(),
});

export type ZodToggleRecurringTemplateSchema = z.infer<typeof toggleRecurringTemplateSchema>;

// =============================================================================
// FULL SCHEMA (for API responses)
// =============================================================================

export const recurringTemplateSchema = baseRecurringTemplateSchema.extend({
  id: z.string(),
  isActive: z.boolean(),
  occurrencesGenerated: z.number(),
  lastGeneratedAt: z.date().optional().nullable(),
  lastGeneratedInvoiceId: z.string().optional().nullable(),
  organizationId: z.string(),
  userId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type RecurringTemplateSchema = z.infer<typeof recurringTemplateSchema>;

// =============================================================================
// DEFAULT VALUES
// =============================================================================

// Extract shared fields from invoice defaults (excluding invoice-specific ones)
const {
  invoiceNumber: _invoiceNumber,
  estimateId: _estimateId,
  worklogIds: _worklogIds,
  status: _status,
  dueDate: _dueDate,
  ...sharedInvoiceDefaults
} = createInvoiceSchemaDefaultValues;

export const createRecurringTemplateSchemaDefaultValues: Partial<ZodCreateRecurringTemplateSchema> = {
  // Inherit shared fields from invoice defaults
  ...sharedInvoiceDefaults,

  // Recurring-specific fields
  name: "",
  frequency: "monthly",
  customIntervalDays: undefined,
  dayOfWeek: undefined,
  dayOfMonth: undefined,
  monthOfYear: undefined,
  nextIssueDate: new Date(),
  sendTime: "09:00",
  endType: "never",
  endDate: undefined,
  maxOccurrences: undefined,
  autoSend: false,
  daysBeforeDue: 30,
  // Email template defaults
  emailTo: undefined,
  emailSubject: undefined,
  emailBody: undefined,
  emailCc: undefined,
  emailBcc: undefined,
};
